import { Injectable, NgZone, Signal, computed, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Network } from '@capacitor/network';
import { Observable, filter, map, pairwise } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppStateService } from './app-state.service';
import { PlatformService } from './platform.service';

// Anonymous, about 1 KB, and the first call of every start anyway.
const PROBE_URL: string = `${environment.apiUrl}/system.clientsettings.api`;
const PROBE_TIMEOUT_MS: number = 5000;
const PROBE_MAX_DELAY_MS: number = 30000;

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly platform = inject(PlatformService);
  private readonly zone = inject(NgZone);
  private readonly mainDataLoaded = toSignal(inject(AppStateService).mainDataLoaded$, { initialValue: false });

  // WebKit's navigator.onLine lags the OS by seconds after a reconnect, so on
  // native the plugin's answer below is the first one that counts. Seeded
  // false it showed the offline page at every reload (iPhone, 2026-10-20).
  private readonly connected = signal<boolean>(this.platform.isNative || navigator.onLine);
  // A request that never got a response while the OS still reports a
  // connection: captive portal, DNS, server down. Also set on reconnection
  // until the backend answers.
  private readonly unreachable = signal<boolean>(false);

  /** True while requests cannot reach the backend. */
  readonly offline: Signal<boolean> = computed(() => !this.connected() || this.unreachable());

  // Latched: the startup calls that failed are not retried, so only a reload
  // (Retry, or the network coming back) or a completed data chain ends it.
  private readonly blocked = signal<boolean>(false);

  /** Offline with nothing usable on screen: login, startup and the domain switch all need the backend. */
  readonly blocking: Signal<boolean> = this.blocked.asReadonly();

  // Whatever failed while offline is not retried on its own: a page that asked
  // the backend once is stuck on its error until something asks again.
  /** Fires once each time the backend is reachable again. */
  readonly backOnline$: Observable<void> = toObservable(this.offline).pipe(
    pairwise(),
    filter(([was, is]) => was && !is),
    map(() => undefined)
  );

  private probing: boolean = false;
  private wakeProbe: (() => void) | null = null;

  constructor() {
    effect(() => {
      if (this.mainDataLoaded()) {
        this.blocked.set(false);
      } else if (this.offline()) {
        this.blocked.set(true);
      }
    });

    if (this.platform.isNative) {
      void Network.getStatus().then(status => this.zone.run(() => this.setConnected(status.connected)));
      void Network.addListener('networkStatusChange', status =>
        this.zone.run(() => this.setConnected(status.connected)));
    } else {
      window.addEventListener('online', () => this.setConnected(true));
      window.addEventListener('offline', () => this.setConnected(false));
    }
  }

  /** From the interceptor: whether the last request got any response at all. */
  reportRequest(reachedServer: boolean): void {
    this.unreachable.set(!reachedServer);
    if (!reachedServer) {
      this.verifyReachable();
    }
  }

  // The OS reports a connection before it carries anything, and on iOS repeats
  // it while Wi-Fi joins: those events only trigger a check, the backend's
  // answer is what ends the offline state.
  private setConnected(connected: boolean): void {
    const wasConnected: boolean = this.connected();
    this.connected.set(connected);

    if (!connected) {
      return;
    }
    if (!wasConnected) {
      this.unreachable.set(true);
    }
    this.verifyReachable();
  }

  // One probe loop at a time, backing off to PROBE_MAX_DELAY_MS with jitter. A
  // new trigger cuts the current wait short.
  private async verifyReachable(): Promise<void> {
    if (!this.unreachable() || !this.connected()) {
      return;
    }
    if (this.probing) {
      this.wakeProbe?.();
      return;
    }

    this.probing = true;
    let attempt: number = 0;
    try {
      while (this.unreachable() && this.connected()) {
        if (await this.probe()) {
          this.unreachable.set(false);
          return;
        }
        const delayMs: number = Math.min(1000 * 2 ** attempt, PROBE_MAX_DELAY_MS) * (0.5 + Math.random() / 2);
        attempt++;
        await new Promise<void>(resolve => {
          const timer: ReturnType<typeof setTimeout> = setTimeout(resolve, delayMs);
          this.wakeProbe = () => {
            clearTimeout(timer);
            attempt = 0;
            resolve();
          };
        });
        this.wakeProbe = null;
      }
    } finally {
      this.probing = false;
    }
  }

  // Any status under 500 is an answer from the backend; 5xx is IIS or a proxy
  // in front of a backend that is not there.
  private async probe(): Promise<boolean> {
    // Not AbortSignal.timeout(): that only exists from iOS 16, the app runs on 15.
    const abort = new AbortController();
    const timer: ReturnType<typeof setTimeout> = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
    try {
      const response: Response = await fetch(`${PROBE_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        signal: abort.signal
      });
      return response.status < 500;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }
}
